import React, { useEffect, useState } from 'react';
import { useCreateMedia, useUpdateMedia } from '@/hooks/api';
import { Media } from '@/prisma/utils/media';
import { useDisclosure } from '@chakra-ui/react';
import { MediaForm } from './form';
import Modal, { ModalSaveEventHandler } from '../modal';
import { MediaType } from '@/prisma/utils/media-type';
import { MediaLevels } from '@/prisma/utils/media-levels';
import {
	Category, MediaLevel, Prisma, Tag 
} from '.prisma/client';

export function useMediaEditor() {
	const {
		isOpen, onOpen: openEditor, onClose: closeEditor, 
	} = useDisclosure();

	return {
		isOpen,
		openEditor,
		closeEditor,
		Editor: MediaEditor,
	};
}

type MediaEditorProps = {
	isOpen: boolean;
	openEditor: () => void;
	closeEditor: () => void;
	media: Media;
	mediaTypes: MediaType[];
	mediaLevels: MediaLevels;
	mediaCategories: Category[];
	mediaDurations: number[];
}

export const MediaEditor: React.FC<MediaEditorProps> = (props: MediaEditorProps) => {
	const {
		isOpen, media, mediaTypes, mediaLevels, mediaDurations, mediaCategories, openEditor, closeEditor 
	} = props;

	const initialRef = React.useRef();
	const finalRef = React.useRef();

	const [name, setName] = useState(media?.name);
	const [description, setDescription] = useState(media?.description);
	const [type, setType] = useState<MediaType>(media?.type as MediaType);
	const [level, setLevel] = useState(media?.level);
	const [categories, setCategories] = useState(media?.categories || []);
	const [tags, setTags] = useState<(Tag | Prisma.TagCreateInput)[]>(media?.tags || []);

	const [duration, setDuration] = useState(media?.duration);
	const [errorMessage, setErrorMessage] = useState('');

	const createMedia = useCreateMedia();
	const updateMedia = useUpdateMedia();
	
	const handleCreateMedia: ModalSaveEventHandler = () => {
		setErrorMessage('');
		createMedia.mutate({
			slug: null,
			name,
			description,
			type,
			level,
			duration,
			categories,
			tags,
		}, {
			onSuccess: closeEditor,
			onError: (e) => {
				setErrorMessage(e.error.message);
			},
		});
	};

	const handleUpdateMedia: ModalSaveEventHandler = () => {
		setErrorMessage('');
		updateMedia.mutate({
			id: media.id,
			slug: null,
			sourceUrl: media.sourceUrl,
			name,
			description,
			type: type.slug,
			level,
			duration,
			categories,
			tags,
		}, {
			onSuccess: closeEditor,
			onError: (e) => setErrorMessage(e.message),
		});
	};

	useEffect(() => {
		setName(media?.name);
		setDescription(media?.description);
		setType(media?.type);
		setLevel(media?.level);
		setDuration(media?.duration);
		setTags(media?.tags || []);
		setCategories(media?.categories || []);
	}, [media]);

	const form = <MediaForm
		name={name}
		onNameChange={(e) => setName(e.target.value)}
		description={description}
		onDescriptionChange={(e) => setDescription(e.target.value)}
		type={type}
		types={mediaTypes}
		onTypeChange={(_t) => setType(_t)}
		level={level}
		levels={mediaLevels}
		onLevelChange={(e) => setLevel(e.target.value as MediaLevel)}
		duration={duration}
		durations={mediaDurations}
		onDurationChange={(e) => setDuration(parseInt(e.target.value) as number)}
		categories={mediaCategories}
		selectedCategories={categories}
		onCategoriesChange={(_cs) => setCategories(_cs)}
		tags={tags as Tag[]}
		onTagsChange={(_ts) => setTags(_ts)}
		sourceUrl={media?.sourceUrl}
		initialRef={initialRef}
		errorMessage={errorMessage}
	/>;

	return (
		<>
			{!media && <Modal
				initialRef={initialRef}
				title="Add media"
				isOpen={isOpen}
				onCancel={closeEditor}
				onSubmit={handleCreateMedia}
			>
				{form}
			</Modal>}
			{media && <Modal
				initialRef={initialRef}
				title={`Edit media ${media.id}`}
				isOpen={isOpen}
				onSubmit={handleUpdateMedia}
				onCancel={closeEditor}
			>
				{form}
			</Modal>}
		</>
	);
};
